// components/proforma/ListeProformas.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Grid,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  InputAdornment
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  FileCopy as DuplicateIcon,
  Print as PrintIcon,
  CheckCircle as ConvertIcon,
  Cancel as CancelIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import ProformaService from '../../services/ProformaService';
import { useSelector } from 'react-redux';
import generatePDFBon from '../../utils/factureUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: theme.spacing(1),
  },
  searchContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  tableContainer: {
    marginBottom: theme.spacing(3),
  },
  statusChip: {
    margin: theme.spacing(0.5),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  totalCard: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  filterSection: {
    marginBottom: theme.spacing(2),
  },
}));

// Statuts des proformas
const PROFORMA_STATUS = {
  BROUILLON: 'BROUILLON',
  ENVOYE: 'ENVOYE',
  CONVERTI: 'CONVERTI',
  ANNULE: 'ANNULE'
};

const STATUS_COLORS = {
  BROUILLON: 'default',
  ENVOYE: 'primary',
  CONVERTI: 'success',
  ANNULE: 'error'
};

const STATUS_LABELS = {
  BROUILLON: 'Brouillon',
  ENVOYE: 'Envoyé',
  CONVERTI: 'Converti',
  ANNULE: 'Annulé'
};

const ListeProformas = () => {
  const classes = useStyles();
  const navigate = useNavigate(); // Remplacé useHistory par useNavigate
  const auth = useSelector((state) => state.auth);
  
  const [proformas, setProformas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('TOUS');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProforma, setSelectedProforma] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchProformas();
  }, [page, rowsPerPage, statusFilter, dateDebut, dateFin]);

  const fetchProformas = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };

      if (statusFilter !== 'TOUS') {
        params.statut = statusFilter;
      }

      if (dateDebut) {
        params.dateDebut = dateDebut;
      }

      if (dateFin) {
        params.dateFin = dateFin;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await ProformaService.getAllProformas(params);
      
      setProformas(response.data.proformas || []);
      setTotalItems(response.pagination?.total || 0);
    } catch (error) {
      console.error('Erreur chargement proformas:', error);
      toast.error('Erreur lors du chargement des proformas');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, proforma) => {
    setAnchorEl(event.currentTarget);
    setSelectedProforma(proforma);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProforma(null);
  };

  const handleEdit = (proforma) => {
    navigate(`/proforma/editer/${proforma.idProforma}`); // Utiliser navigate au lieu de history.push
  };

  const handleView = (proforma) => {
    navigate(`/proforma/visualiser/${proforma.idProforma}`); // Utiliser navigate au lieu de history.push
  };

  const handleDuplicate = async () => {
    if (!selectedProforma) return;

    try {
      const response = await ProformaService.duplicateProforma(selectedProforma.idProforma, {
        speudo: auth.nom,
        modifications: {
          nomClient: `Copie de ${selectedProforma.nomClient}`,
          dateProforma: new Date()
        }
      });

      toast.success('Proforma dupliqué avec succès');
      fetchProformas();
    } catch (error) {
      console.error('Erreur duplication:', error);
      toast.error(error.error || 'Erreur lors de la duplication');
    } finally {
      handleMenuClose();
    }
  };

  const handleConvert = async () => {
    if (!selectedProforma) return;

    setConverting(true);
    try {
      await ProformaService.convertToFacture(selectedProforma.idProforma, {
        speudo: auth.nom
      });

      toast.success('Proforma converti en facture avec succès');
      fetchProformas();
    } catch (error) {
      console.error('Erreur conversion:', error);
      toast.error(error.error || 'Erreur lors de la conversion');
    } finally {
      setConverting(false);
      handleMenuClose();
    }
  };

  const handleCancel = async () => {
    if (!selectedProforma) return;

    if (window.confirm('Êtes-vous sûr de vouloir annuler ce proforma ?')) {
      try {
        await ProformaService.updateProformaStatus(selectedProforma.idProforma, {
          statut: PROFORMA_STATUS.ANNULE,
          speudo: auth.nom,
          description: 'Annulation manuelle'
        });

        toast.success('Proforma annulé avec succès');
        fetchProformas();
      } catch (error) {
        console.error('Erreur annulation:', error);
        toast.error(error.error || 'Erreur lors de l\'annulation');
      } finally {
        handleMenuClose();
      }
    }
  };

  const handleDelete = () => {
    setDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedProforma) return;

    try {
      await ProformaService.deleteProforma(selectedProforma.idProforma, {
        speudo: auth.nom,
        raison: 'Suppression manuelle'
      });

      toast.success('Proforma supprimé avec succès');
      fetchProformas();
    } catch (error) {
      console.error('Erreur suppression:', error);
      toast.error(error.error || 'Erreur lors de la suppression');
    } finally {
      setDeleteDialog(false);
      handleMenuClose();
    }
  };

  const handleSendEmail = () => {
    setEmailDialog(true);
  };

  const confirmSendEmail = async () => {
    if (!selectedProforma || !emailAddress) return;

    try {
      await ProformaService.sendProformaByEmail(selectedProforma.idProforma, {
        email: emailAddress,
        speudo: auth.nom,
        message: `Bonjour,\n\nVeuillez trouver ci-joint le proforma ${selectedProforma.numero}.\n\nCordialement`
      });

      toast.success('Proforma envoyé par email avec succès');
      setEmailAddress('');
      fetchProformas();
    } catch (error) {
      console.error('Erreur envoi email:', error);
      toast.error(error.error || 'Erreur lors de l\'envoi');
    } finally {
      setEmailDialog(false);
      handleMenuClose();
    }
  };

  const handleGeneratePDF = async (proforma) => {
    try {
      // Charger les détails du proforma
      const response = await ProformaService.getProformaById(proforma.idProforma);
      const proformaDetails = response.data.proforma;
      const lignes = response.data.lignes;
      console.log(response)

      // Préparer les données pour le PDF
      const pdfData = {
        reportProd: lignes.map((ligne, index) => ({
          id: index + 1,
          nomProd: ligne.nomProduit,
          qtVdu: ligne.quantite,
          prixProdVendu: parseInt(ligne.prixUnitaire),
          total: parseInt(ligne.totalLigne),
          dateEnreg: new Date(proformaDetails.dateProforma),
          beneficiaire: '',
          nom: proformaDetails.nomClient,
          adresse: proformaDetails.adresseClient || '',
          telephone: proformaDetails.telephoneClient || '',
          numFactSrt: proformaDetails.numero,
          vendeur: proformaDetails.speudo,
          statut: proformaDetails.statut,
          remise: 0
        })),
        clientInfo: {
          nom: proformaDetails.nomClient,
          designation: 'PROFORMA'
        }
      };

      // Importer dynamiquement la fonction de génération PDF
      console.log("pdfData.reportProd")
      console.log(pdfData.reportProd)
      console.log("pdfData.reportProd")
      generatePDFBon(pdfData.reportProd, pdfData.clientInfo);
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handlePrint = (proforma) => {
    toast.info('Fonctionnalité d\'impression à implémenter');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 F';
    return `${parseFloat(amount).toLocaleString('fr-FR')} F`;
  };

  const getStatusStats = () => {
    const stats = {
      BROUILLON: 0,
      ENVOYE: 0,
      CONVERTI: 0,
      ANNULE: 0,
      TOTAL: proformas.length
    };

    proformas.forEach(proforma => {
      if (stats.hasOwnProperty(proforma.statut)) {
        stats[proforma.statut]++;
      }
    });

    return stats;
  };

  const statusStats = getStatusStats();

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            Chargement des proformas...
          </Typography>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.header} elevation={3}>
        <Typography variant="h4" gutterBottom>
          Liste des Proformas
        </Typography>
        <Typography variant="body1">
          Gestion des devis et propositions commerciales
        </Typography>
      </Paper>

      {/* Filtres et recherche */}
      <Paper className={classes.searchContainer} elevation={1}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher par client, numéro..."
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              type="date"
              label="Date début"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              type="date"
              label="Date fin"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('TOUS');
                setDateDebut('');
                setDateFin('');
                setPage(0);
                fetchProformas();
              }}
            >
              Actualiser
            </Button>
          </Grid>
        </Grid>

        {/* Filtres par statut */}
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Filtrer par statut:
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Chip
              label={`Tous (${statusStats.TOTAL})`}
              onClick={() => handleStatusFilter('TOUS')}
              color={statusFilter === 'TOUS' ? 'primary' : 'default'}
              variant={statusFilter === 'TOUS' ? 'default' : 'outlined'}
            />
            <Chip
              label={`Brouillon (${statusStats.BROUILLON})`}
              onClick={() => handleStatusFilter(PROFORMA_STATUS.BROUILLON)}
              color={statusFilter === PROFORMA_STATUS.BROUILLON ? 'primary' : 'default'}
              variant={statusFilter === PROFORMA_STATUS.BROUILLON ? 'default' : 'outlined'}
            />
            <Chip
              label={`Envoyés (${statusStats.ENVOYE})`}
              onClick={() => handleStatusFilter(PROFORMA_STATUS.ENVOYE)}
              color={statusFilter === PROFORMA_STATUS.ENVOYE ? 'primary' : 'default'}
              variant={statusFilter === PROFORMA_STATUS.ENVOYE ? 'default' : 'outlined'}
            />
            <Chip
              label={`Convertis (${statusStats.CONVERTI})`}
              onClick={() => handleStatusFilter(PROFORMA_STATUS.CONVERTI)}
              color={statusFilter === PROFORMA_STATUS.CONVERTI ? 'primary' : 'default'}
              variant={statusFilter === PROFORMA_STATUS.CONVERTI ? 'default' : 'outlined'}
            />
            <Chip
              label={`Annulés (${statusStats.ANNULE})`}
              onClick={() => handleStatusFilter(PROFORMA_STATUS.ANNULE)}
              color={statusFilter === PROFORMA_STATUS.ANNULE ? 'primary' : 'default'}
              variant={statusFilter === PROFORMA_STATUS.ANNULE ? 'default' : 'outlined'}
            />
          </Box>
        </Box>
      </Paper>

      {/* Statistiques */}
      <Grid container spacing={2} className={classes.filterSection}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.totalCard}>
            <Typography variant="subtitle2" color="textSecondary">
              Total Proformas
            </Typography>
            <Typography variant="h5">
              {statusStats.TOTAL}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.totalCard}>
            <Typography variant="subtitle2" color="textSecondary">
              En attente
            </Typography>
            <Typography variant="h5" style={{ color: '#ff9800' }}>
              {statusStats.BROUILLON}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.totalCard}>
            <Typography variant="subtitle2" color="textSecondary">
              Convertis
            </Typography>
            <Typography variant="h5" style={{ color: '#4caf50' }}>
              {statusStats.CONVERTI}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper className={classes.totalCard}>
            <Typography variant="subtitle2" color="textSecondary">
              Chiffre d'affaires
            </Typography>
            <Typography variant="h5" style={{ color: '#2196f3' }}>
              {formatCurrency(proformas.reduce((sum, p) => sum + parseFloat(p.totalTTC || 0), 0))}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tableau des proformas */}
      <Paper className={classes.tableContainer} elevation={1}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Numéro</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell align="right"><strong>Montant</strong></TableCell>
                <TableCell><strong>Statut</strong></TableCell>
                <TableCell><strong>Créé par</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proformas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Box className={classes.emptyState}>
                      <Typography variant="h6" color="textSecondary">
                        Aucun proforma trouvé
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Créez votre premier proforma en cliquant sur "Nouveau Proforma"
                      </Typography>
                      {/* <Box mt={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          component={Link}
                          to="/proforma/nouveau"
                        >
                          Nouveau Proforma
                        </Button>
                      </Box> */}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                proformas.map((proforma) => (
                  <TableRow key={proforma.idProforma} hover>
                    <TableCell>
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {proforma.numero}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {proforma.nomClient}
                      </Typography>
                      {proforma.telephoneClient && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {proforma.telephoneClient}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(proforma.dateProforma)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        {proforma.totalTTC}
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        {proforma.nbLignes || 0} articles
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={STATUS_LABELS[proforma.statut] || proforma.statut}
                        color={STATUS_COLORS[proforma.statut] || 'default'}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {proforma.speudo}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        {/* <Tooltip title="Voir">
                          <IconButton
                            size="small"
                            onClick={() => handleView(proforma)}
                            className={classes.actionButton}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip> */}
                        
                        {/* {proforma.statut === PROFORMA_STATUS.BROUILLON && (
                          <Tooltip title="Éditer">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(proforma)}
                              className={classes.actionButton}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )} */}

                        <Tooltip title="Générer PDF">
                          <IconButton
                            size="small"
                            onClick={() => handleGeneratePDF(proforma)}
                            className={classes.actionButton}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, proforma)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} sur ${count}`
          }
        />
      </Paper>

      {/* Menu d'actions contextuel */}
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {/* <MenuItem onClick={() => selectedProforma && handleView(selectedProforma)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Voir détails" />
        </MenuItem> */}
        
        {selectedProforma?.statut === PROFORMA_STATUS.BROUILLON && (
          <MenuItem onClick={() => selectedProforma && handleEdit(selectedProforma)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Éditer" />
          </MenuItem>
        )}

        {/* <MenuItem onClick={handleDuplicate}>
          <ListItemIcon>
            <DuplicateIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Dupliquer" />
        </MenuItem> */}

        <MenuItem onClick={() => selectedProforma && handleGeneratePDF(selectedProforma)}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Télécharger PDF" />
        </MenuItem>

        <MenuItem onClick={() => selectedProforma && handlePrint(selectedProforma)}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Imprimer" />
        </MenuItem>

        {selectedProforma?.statut === PROFORMA_STATUS.BROUILLON && (
          <MenuItem onClick={handleSendEmail}>
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Envoyer par email" />
          </MenuItem>
        )}

        {selectedProforma?.statut === PROFORMA_STATUS.BROUILLON && (
          <MenuItem onClick={handleConvert} disabled={converting}>
            <ListItemIcon>
              {converting ? <CircularProgress size={20} /> : <ConvertIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText primary={converting ? "Conversion..." : "Convertir en facture"} />
          </MenuItem>
        )}

        {selectedProforma?.statut === PROFORMA_STATUS.BROUILLON && (
          <MenuItem onClick={handleCancel}>
            <ListItemIcon>
              <CancelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Annuler" />
          </MenuItem>
        )}

        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Supprimer" />
        </MenuItem>
      </Menu>

      {/* Dialogue de suppression */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir supprimer le proforma {selectedProforma?.numero} ?
            Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={confirmDelete} color="secondary" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue d'envoi email */}
      <Dialog
        open={emailDialog}
        onClose={() => setEmailDialog(false)}
      >
        <DialogTitle>Envoyer le proforma par email</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Adresse email"
            type="email"
            fullWidth
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={confirmSendEmail} color="primary" variant="contained">
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bouton nouveau proforma */}
      {/* <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          component={Link}
          to="/proforma/nouveau"
          size="large"
        >
          Nouveau Proforma
        </Button>
      </Box> */}
    </Container>
  );
};

export default ListeProformas;